<script setup lang="ts">
// @ts-expect-error Not a ts file
import store from '@/store'
import IconTier from '@/features/tickets/assets/IconTier.vue'
import { ITicketPurchased } from '@/features/tickets/types'
import { computed, ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { mdiCheck, mdiDotsHorizontal } from '@mdi/js'
import BKConfirmationDialog from '@/shared/ui/modals/BKConfirmationDialog.vue'
import {
  useCheckInTicketMutation,
  useRemoveTicketMutation,
  useUncheckTicketMutation,
} from '@/features/tickets/api/queries'

const props = defineProps<{
  ticket: ITicketPurchased
}>()

defineEmits<{
  (e: 'show-assignment-interface'): void
  (e: 'show-tier-change-interface'): void
  (e: 'show-refund-interface'): void
  (e: 'show-receipt-interface'): void
}>()

const getUserDB = computed(() => store.getters.getUserDB)
const { getImageById } = useImage()
const { mutate: checkInTicket, isPending: isCheckingIn, error: checkInError } = useCheckInTicketMutation()
const { mutate: uncheckTicket, isPending: isUnchecking, error: uncheckError } = useUncheckTicketMutation()
const { mutate: removeTicket, isPending: isRemoving, error: removeError } = useRemoveTicketMutation()

const actionError = computed(() => checkInError.value || uncheckError.value || removeError.value)

const showUncheckConfirm = ref(false)
const showRemoveConfirm = ref(false)

const checkInTicketHandler = async () => {
  if (isCheckingIn.value) return
  checkInTicket(props.ticket.id)
}

const confirmUncheck = () => {
  showUncheckConfirm.value = false
  if (isUnchecking.value) return
  uncheckTicket(props.ticket.id)
}

const confirmRemove = () => {
  showRemoveConfirm.value = false
  if (isRemoving.value) return
  removeTicket(props.ticket.id)
}
</script>

<template>
  <div class="selected-ticket-container">
    <div class="ticket-top-container">
      <div class="tier-info-section">
        <div class="tier-info">
          <p class="tier-label">Ticket tier</p>
          <div class="tier-item">
            <IconTier />
            <div>
              <p class="tier-title">{{ ticket.title }}</p>
            </div>
          </div>
        </div>
        <button
          v-if="ticket.canChangeTier"
          class="change-tier-button"
          :disabled="!ticket.canChangeTier"
          @click="$emit('show-tier-change-interface')"
        >
          Change ticket tier
        </button>
      </div>
      <div class="tier-assignment-section">
        <div class="tier-assignment-info">
          <p class="assignment-label">Assigned to</p>
          <div class="assigned-user-info">
            <AvatarPlaceholder
              v-if="!ticket.assignedTo || !('avatar' in ticket.assignedTo) || !ticket.assignedTo.avatar"
              background-color="#DDDDDD"
              color="#333333"
            />
            <v-avatar
              v-else
              size="20"
            >
              <v-img :src="getImageById(ticket.assignedTo.avatar, 40, 40, 'avatar')" />
            </v-avatar>
            <p class="assigned-user-details">
              <span class="assigned-user-name">
                {{
                  !ticket.assignedTo
                    ? 'Unassigned'
                    : 'dancerName' in ticket.assignedTo
                      ? ticket.assignedTo.dancerName
                      : ticket.assignedTo.email || 'Unnamed User'
                }}
              </span>
              <span
                v-if="ticket.assignedTo?.id === getUserDB.id"
                class="assigned-to-you"
              >
                (You)
              </span>
            </p>
          </div>
        </div>
        <button
          v-if="ticket.canReassign"
          class="change-assignment-button"
          @click="$emit('show-assignment-interface')"
        >
          {{ ticket.assignedTo ? 'Re-assign ticket' : 'Assign ticket' }}
        </button>
      </div>
    </div>

    <BKButton
      v-if="!ticket.isCheckedIn"
      color="var(--color-green)"
      white-text
      width="217px"
      :loading="isCheckingIn"
      @click="checkInTicketHandler"
    >
      Check in
    </BKButton>

    <div
      v-if="ticket.isCheckedIn"
      class="check-in-status"
    >
      <div class="status-title">
        Checked In
        <v-icon color="var(--color-green)">{{ mdiCheck }}</v-icon>
      </div>
      <div class="status-row">
        <span class="row-title">Checked in by</span>
        <div
          v-if="ticket.checkedInBy"
          class="row-value checked-in-by"
        >
          <v-avatar size="20">
            <v-img :src="getImageById(ticket.checkedInBy.avatar, 40, 40, 'avatar')" />
          </v-avatar>
          <span class="checked-in-user-name">
            {{
              typeof ticket.checkedInBy === 'object'
                ? 'dancerName' in ticket.checkedInBy
                  ? ticket.checkedInBy.dancerName
                  : ticket.checkedInBy.email || 'Unnamed User'
                : 'N/A'
            }}
          </span>
        </div>
      </div>
      <div class="status-row">
        <span class="row-title">Checked in at</span>
        <div class="row-value">
          {{ ticket.checkedInAt ? new Date(ticket.checkedInAt).toLocaleString() : 'N/A' }}
        </div>
      </div>
    </div>

    <div class="more-actions-section">
      More ticket actions
      <v-menu
        content-class="more-ticket-actions-menu"
        top
        left
      >
        <template #activator="{ props: p, on }">
          <v-btn
            v-bind="p"
            icon
            class="ticket-actions-button"
            v-on="on"
          >
            <v-icon>{{ mdiDotsHorizontal }}</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="$emit('show-receipt-interface')"> View receipt </v-list-item>
          <v-list-item
            v-if="ticket.isCheckedIn"
            @click="showUncheckConfirm = true"
          >
            Uncheck ticket
          </v-list-item>
          <v-list-item
            v-if="ticket.canRefund"
            class="refund-ticket-item"
            @click="$emit('show-refund-interface')"
          >
            Refund ticket
          </v-list-item>
          <v-list-item
            v-if="ticket.canRemove"
            class="refund-ticket-item"
            @click="showRemoveConfirm = true"
          >
            Remove ticket
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <BKConfirmationDialog
      :is-show="showUncheckConfirm"
      title="Uncheck ticket?"
      confirm-text="Uncheck"
      @cancel="showUncheckConfirm = false"
      @confirm="confirmUncheck"
    >
      The attendee will no longer be marked as checked in for this ticket.
    </BKConfirmationDialog>

    <BKConfirmationDialog
      :is-show="showRemoveConfirm"
      title="Remove ticket?"
      confirm-text="Remove"
      @cancel="showRemoveConfirm = false"
      @confirm="confirmRemove"
    >
      This undoes the ticket entirely, as if it was never added. This cannot be undone.
    </BKConfirmationDialog>

    <v-snackbar
      :value="!!actionError"
      color="var(--rasberry-red)"
    >
      {{ actionError?.message || 'An error occurred. Please try again.' }}
    </v-snackbar>
  </div>
</template>

<style scoped lang="scss">
.selected-ticket-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
  height: 100%;

  p {
    margin: 0;
  }

  .ticket-top-container {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 30px;

    .tier-info-section,
    .tier-assignment-section {
      display: flex;
      gap: 20px;
      justify-content: space-between;
      align-items: flex-end;
    }

    .tier-info,
    .tier-assignment-info {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .tier-label,
      .assignment-label {
        font-size: 14px;
        line-height: 20px;
        color: var(--color-grey-300);
      }

      .tier-item {
        display: flex;
        gap: 8px;

        .tier-title {
          font-weight: 500;
          font-size: 16px;
          line-height: 1.3;
          color: var(--color-dark-800);
        }

        .tier-description {
          font-size: 12px;
          line-height: 14px;
          color: var(--color-grey-300);
        }
      }

      .assigned-user-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .assigned-user-details {
          .assigned-user-name {
            font-weight: 500;
            font-size: 16px;
            line-height: 24px;
            color: var(--color-dark-800);
          }

          .assigned-to-you {
            font-size: 16px;
            line-height: 24px;
            color: var(--color-grey-300);
          }
        }
      }
    }

    .change-tier-button,
    .change-assignment-button {
      font-weight: 500;
      font-size: 14px;
      line-height: 1.3;
      color: var(--sky-blue);
      text-decoration: underline;
      cursor: pointer;
      transition: filter 0.2s;

      &:hover,
      &:focus-visible {
        filter: brightness(0.8);
      }

      &:active {
        filter: brightness(0.6);
      }

      &:disabled {
        color: var(--color-grey-200);
        text-decoration: none;
        cursor: default;
        filter: none;
      }
    }
  }

  .more-actions-section {
    align-self: stretch;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.3;
    color: var(--color-dark-800);
  }

  .check-in-status {
    align-self: stretch;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 14px;
    background-color: var(--color-white);
    border-radius: 6px;

    .status-title {
      display: flex;
      align-items: center;
      gap: 45px;
      font-family: ff-good-web-pro, Roboto, Helvetica, sans-serif;
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-green);
      text-transform: uppercase;
    }

    .status-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }

      .row-title {
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
        color: var(--color-dark-800);
      }

      .row-value {
        font-size: 14px;
        line-height: 1.2;
        color: var(--color-grey-300);

        &.checked-in-by {
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
    }
  }
}

.more-ticket-actions-menu {
  border-radius: 6px;
  box-shadow: 0 1px 4px rgb(0 0 0 / 20%);

  .refund-ticket-item {
    color: var(--rasberry-red) !important;
  }
}
</style>
